import { useState } from 'react';

type Statistic = { name: string, data: any };

// Persists which "More Statistics" charts a user has chosen to display, per instance.
// `scope` distinguishes independent pickers for the same instance (e.g. the instance-level
// SQL Statistics tab vs. a per-statement SQL Statistics tab), so their selections don't collide.
export function useSqlStatisticsSelection(instanceId: number, scope: string = 'instance') {
    const [supportedStatistics, setSupportedStatisticsState] = useState<Statistic[]>([]);
    const [statisticsToShow, setStatisticsToShow] = useState<Statistic[]>([]);

    const storageKey = `sqlStatistics_${scope}_${instanceId}_selectedStats`;

    const persistSelection = (list: Statistic[]) => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(list.map(item => item.name)));
        } catch (error) {
            console.warn('Failed to save SQL statistics selection to localStorage:', error);
        }
    };

    // Call once new supportedStatistics have been fetched - restores the saved
    // selection, filtered down to whichever statistics are still supported.
    const setSupportedStatistics = (stats: Statistic[]) => {
        setSupportedStatisticsState(stats);

        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const savedNames: string[] = JSON.parse(saved);
                setStatisticsToShow(stats.filter(stat => savedNames.includes(stat.name)));
            }
        } catch (error) {
            console.warn('Failed to restore SQL statistics selection from localStorage:', error);
        }
    };

    const updateSqlExtendedOptionsList = (value: Statistic) => {
        const exists = statisticsToShow.find(item => item.name === value.name);
        const updatedList = exists
                ? statisticsToShow.filter(item => item.name !== exists.name)
                : [...statisticsToShow, value];

        setStatisticsToShow(updatedList);
        persistSelection(updatedList);
    };

    const checkUncheckAll = (checkState: boolean) => {
        const updatedList = checkState ? supportedStatistics : [];
        setStatisticsToShow(updatedList);
        persistSelection(updatedList);
    };

    return {
        supportedStatistics,
        setSupportedStatistics,
        statisticsToShow,
        updateSqlExtendedOptionsList,
        checkUncheckAll
    };
}
